import React from 'react';
import { cn } from '../../lib/utils';

// Base Skeleton Component
interface SkeletonProps {
  className?: string;
  animate?: boolean;
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className, animate = true, style }) => {
  return (
    <div
      className={cn(
        'bg-slate-800/50 rounded-lg',
        animate && 'animate-pulse',
        className
      )}
      style={style}
    />
  );
};

// Skeleton for text lines
interface SkeletonTextProps {
  lines?: number;
  className?: string;
  lineClassName?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  className,
  lineClassName
}) => {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 ? 'w-3/4' : 'w-full',
            lineClassName
          )}
        />
      ))}
    </div>
  );
};

// Skeleton for cards (like KPI cards)
interface SkeletonCardProps {
  className?: string;
  showIcon?: boolean;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ className, showIcon = true }) => {
  return (
    <div className={cn('p-6 rounded-2xl bg-slate-900/50 border border-white/5', className)}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Skeleton className="h-3 w-20 mb-3" />
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-3 w-24" />
        </div>
        {showIcon && (
          <Skeleton className="w-12 h-12 rounded-xl" />
        )}
      </div>
    </div>
  );
};

// Skeleton for table rows
interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
  showHeader?: boolean;
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 5,
  className,
  showHeader = true
}) => {
  return (
    <div className={cn('w-full', className)}>
      {showHeader && (
        <div className="flex gap-4 p-4 border-b border-white/5">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className="h-4 flex-1" />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex gap-4 p-4 border-b border-white/5"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              className={cn(
                'h-4 flex-1',
                colIndex === 0 && 'max-w-[120px]'
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Skeleton for list items
interface SkeletonListProps {
  items?: number;
  className?: string;
  showAvatar?: boolean;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  items = 5,
  className,
  showAvatar = false
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/30">
          {showAvatar && (
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
          )}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Skeleton for chart/graph areas
interface SkeletonChartProps {
  className?: string;
  type?: 'bar' | 'line' | 'area';
}

export const SkeletonChart: React.FC<SkeletonChartProps> = ({
  className,
  type = 'bar'
}) => {
  return (
    <div className={cn('p-6 rounded-2xl bg-slate-900/50 border border-white/5', className)}>
      {/* Title */}
      <Skeleton className="h-5 w-32 mb-4" />

      {/* Chart area */}
      <div className="h-48 flex items-end justify-between gap-2 pt-8">
        {type === 'bar' && (
          <>
            {[40, 65, 45, 80, 55, 70, 35].map((height, i) => (
              <Skeleton
                key={i}
                className="flex-1 rounded-t-lg"
                style={{ height: `${height}%` }}
              />
            ))}
          </>
        )}
        {(type === 'line' || type === 'area') && (
          <Skeleton className="w-full h-full rounded-lg" />
        )}
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-8" />
        ))}
      </div>
    </div>
  );
};

// Skeleton for profile/avatar sections
interface SkeletonAvatarProps {
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
  className?: string;
}

export const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({
  size = 'md',
  showDetails = false,
  className
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Skeleton className={cn('rounded-full', sizeClasses[size])} />
      {showDetails && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      )}
    </div>
  );
};

// Skeleton for form fields
interface SkeletonFormProps {
  fields?: number;
  className?: string;
}

export const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 4,
  className
}) => {
  return (
    <div className={cn('space-y-6', className)}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      ))}
      <Skeleton className="h-10 w-32 rounded-xl mt-4" />
    </div>
  );
};

// Dashboard KPI Skeleton (specific to this app's design)
export const SkeletonKPICard: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cn(
      'p-6 rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-white/5 backdrop-blur-xl',
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="space-y-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-9 w-28" />
          <div className="flex items-center gap-2 mt-2">
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <Skeleton className="w-12 h-12 rounded-xl" />
      </div>
    </div>
  );
};

// Activity Item Skeleton (for recent activity lists)
export const SkeletonActivityItem: React.FC = () => {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/30">
      <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
  );
};

export default Skeleton;
